import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import { format } from 'date-fns';
import { CheckCircle, AlertCircle, Clock, ChevronRight, RefreshCw } from 'lucide-react';
import { FourDEMRTransaction } from '../types';
import { processWebhookTransaction } from '../services/webhookProcessor';
import toast from 'react-hot-toast';

export default function FourDEMRTransactions() {
  const [selectedTransaction, setSelectedTransaction] = React.useState<FourDEMRTransaction | null>(null);
  // Removed autoProcess - backend scheduled job handles auto-processing
  // This page is only for manual retry of failed transactions
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);
  const [processingIds, setProcessingIds] = React.useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['fourdeemr-transactions'],
    queryFn: async () => {
      // Fetch all transactions from emr_transactions, fallback to old table name
      let { data: emrTransactions, error: transactionsError } = await supabase
        .from('emr_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      // If table doesn't exist (migration not applied), try old table name
      if (transactionsError && (transactionsError.code === '42P01' || transactionsError.message?.includes('does not exist'))) {
        console.log('emr_transactions not found, falling back to fourdeemr_transactions...');
        const fallbackResult = await supabase
          .from('fourdeemr_transactions')
          .select('*')
          .order('created_at', { ascending: false });
        emrTransactions = fallbackResult.data;
        transactionsError = fallbackResult.error;
      }

      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
        return [];
      }

      return (emrTransactions || []) as FourDEMRTransaction[];
    },
  });

  // Mutation for processing a transaction
  const processTransaction = useMutation({
    mutationFn: async (transactionId: string) => {
      // Add to processing IDs
      setProcessingIds(prev => [...prev, transactionId]);
      try {
        // First, update the UI to show processing status
        // Try new table first, fallback to old
        let updateResult = await supabase
          .from('emr_transactions')
          .update({ status: 'processing' })
          .eq('id', transactionId);
        
        if (updateResult.error && (updateResult.error.code === '42P01' || updateResult.error.message?.includes('does not exist'))) {
          await supabase
            .from('fourdeemr_transactions')
            .update({ status: 'processing' })
            .eq('id', transactionId);
        }
        
        // Try to update webhook_logs status too
        try {
          await supabase
            .from('webhook_logs')
            .update({ status: 'processing' })
            .eq('id', transactionId);
        } catch (e) {
          // Ignore errors, this is just for UI improvement
        }
        
        // Update the selected transaction if it's the one being processed
        if (selectedTransaction?.id === transactionId) {
          setSelectedTransaction(prev => prev ? { ...prev, status: 'processing' } : null);
        }
        
        // Refresh the UI to show processing status
        queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] });
        
        // Process the transaction
        const result = await processWebhookTransaction(transactionId);
        
        // Force a small delay to ensure the UI updates properly
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Refresh the UI again after processing
        queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] });
        
        return result;
      } finally {
        // Remove from processing IDs when done, regardless of success/failure
        setProcessingIds(prev => prev.filter(id => id !== transactionId));
      }
    },
    onSuccess: (success, transactionId) => {
      // Refresh the data one more time to ensure we have the latest state
      queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] });
      toast.success('Transaction processed successfully');
      
      // Update selected transaction if it was the one processed
      if (selectedTransaction?.id === transactionId) {
        // Fetch the latest transaction data
        const fetchLatestTransaction = async () => {
          try {
            // Try emr_transactions first, fallback to old table
            let { data: transaction, error: fetchError } = await supabase
              .from('emr_transactions')
              .select('*')
              .eq('id', transactionId)
              .maybeSingle();
            
            // Fallback to old table name if new doesn't exist
            if (fetchError && (fetchError.code === '42P01' || fetchError.message?.includes('does not exist'))) {
              const fallbackResult = await supabase
                .from('fourdeemr_transactions')
                .select('*')
                .eq('id', transactionId)
                .maybeSingle();
              transaction = fallbackResult.data;
            }
            
            if (transaction) {
              setSelectedTransaction(transaction);
              return;
            }
            
            // If not found, try webhook_logs
            const { data: webhookLog } = await supabase
              .from('webhook_logs')
              .select('*')
              .eq('id', transactionId)
              .maybeSingle();
            
            if (webhookLog) {
              setSelectedTransaction({
                id: webhookLog.id || transactionId, // Use the transactionId as fallback
                user_id: null as any, // Cast to any to bypass type check since null is acceptable here
                type: webhookLog.type || '',
                payload: webhookLog.payload || {},
                status: webhookLog.status || 'unknown',
                error: webhookLog.error || null,
                created_at: webhookLog.created_at || new Date().toISOString(),
                processed_at: webhookLog.processed_at || null
              });
            }
          } catch (error) {
            console.error('Error fetching updated transaction:', error);
          }
        };
        
        fetchLatestTransaction();
      }
    },
    onError: (error, transactionId) => {
      console.error('Error processing transaction:', error);
      toast.error('Failed to process transaction: ' + (error instanceof Error ? error.message : String(error)));
      
      // Refresh the data to ensure we have the latest state
      queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] });
      
      // Update selected transaction if it was the one processed
      if (selectedTransaction?.id === transactionId) {
        // Fetch the latest transaction data
        const fetchLatestTransaction = async () => {
          try {
            // Try emr_transactions first, fallback to old table
            let { data: transaction, error: fetchError } = await supabase
              .from('emr_transactions')
              .select('*')
              .eq('id', transactionId)
              .maybeSingle();
            
            // Fallback to old table name if new doesn't exist
            if (fetchError && (fetchError.code === '42P01' || fetchError.message?.includes('does not exist'))) {
              const fallbackResult = await supabase
                .from('fourdeemr_transactions')
                .select('*')
                .eq('id', transactionId)
                .maybeSingle();
              transaction = fallbackResult.data;
            }
            
            if (transaction) {
              setSelectedTransaction(transaction);
              return;
            }
            
            // If not found, try webhook_logs
            const { data: webhookLog } = await supabase
              .from('webhook_logs')
              .select('*')
              .eq('id', transactionId)
              .maybeSingle();
            
            if (webhookLog) {
              setSelectedTransaction({
                id: webhookLog.id || transactionId, // Use the transactionId as fallback
                user_id: null as any, // Cast to any to bypass type check since null is acceptable here
                type: webhookLog.type || '',
                payload: webhookLog.payload || {},
                status: webhookLog.status || 'unknown',
                error: webhookLog.error || null,
                created_at: webhookLog.created_at || new Date().toISOString(),
                processed_at: webhookLog.processed_at || null
              });
            }
          } catch (error) {
            console.error('Error fetching updated transaction:', error);
          }
        };
        
        fetchLatestTransaction();
      }
    }
  });

  // Removed toggleAutoProcess - backend handles auto-processing via scheduled job

  // Handle manual refresh with loading state
  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] })
      .then(() => {
        setTimeout(() => setIsRefreshing(false), 500); // Add a small delay to show loading state
        toast.success('Transactions refreshed');
      })
      .catch(() => {
        setIsRefreshing(false);
        toast.error('Failed to refresh transactions');
      });
  };

  // Removed auto-processing effect - backend scheduled job handles this automatically
  // This page is only for manual retry of failed transactions

  // Effect to reset any transactions stuck in 'processing' state back to 'received' state
  useEffect(() => {
    const resetStuckTransactions = async () => {
      if (!transactions) return;
      
      // Find any transactions stuck in 'processing' state for more than 5 minutes
      const now = Date.now();
      const processingTransactions = transactions.filter(t => {
        if (t.status !== 'processing') return false;
        // If created_at exists, check if it's been more than 5 minutes
        if (t.created_at) {
          const createdAt = new Date(t.created_at).getTime();
          return (now - createdAt) > 5 * 60 * 1000; // 5 minutes
        }
        return true; // If no created_at, assume it's stuck
      });
      
      if (processingTransactions.length > 0) {
        console.log(`Found ${processingTransactions.length} transactions stuck in processing state, resetting...`);
        
        // Reset each transaction back to 'received' state
        for (const transaction of processingTransactions) {
          try {
            // Try new table first, fallback to old
            let { error } = await supabase
              .from('emr_transactions')
              .update({ status: 'received' })
              .eq('id', transaction.id);
            
            // Fallback to old table name if new doesn't exist
            if (error && (error.code === '42P01' || error.message?.includes('does not exist'))) {
              const fallbackResult = await supabase
                .from('fourdeemr_transactions')
                .update({ status: 'received' })
                .eq('id', transaction.id);
              error = fallbackResult.error;
            }
              
            if (error) {
              console.error(`Error resetting transaction ${transaction.id}:`, error);
              // Don't throw - continue with other transactions
            } else {
              // Also try to update webhook_logs if it exists
              try {
                await supabase
                  .from('webhook_logs')
                  .update({ status: 'received' })
                  .eq('id', transaction.id);
              } catch (webhookError) {
                // Ignore webhook_logs errors - it's optional
              }
            }
          } catch (err) {
            console.error(`Error resetting transaction ${transaction.id}:`, err);
            // Don't throw - continue with other transactions
          }
        }
        
        // Refresh the transactions list after a short delay
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['fourdeemr-transactions'] });
        }, 500);
      }
    };
    
    resetStuckTransactions();
  }, [transactions, queryClient]);

  const getStatusIcon = (status: FourDEMRTransaction['status']) => {
    switch (status) {
      case 'received':
        return <Clock className="text-yellow-500" />;
      case 'processing':
        return <RefreshCw className="text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="text-green-500" />;
      case 'failed':
        return <AlertCircle className="text-red-500" />;
      default:
        return <Clock className="text-gray-500" />;
    }
  };

  const getStatusClass = (status: FourDEMRTransaction['status']) => {
    switch (status) {
      case 'received':
        return 'text-yellow-500';
      case 'processing':
        return 'text-blue-500';
      case 'completed':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  // Check if a transaction is currently being processed
  const isProcessing = (transactionId: string) => {
    return processingIds.includes(transactionId);
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-2/3">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">4D EMR Transactions</h1>
              <p className="text-gray-600">
                Monitor transactions and manually retry failed items. Auto-processing handled by backend.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Auto-processing handled by backend scheduled job
              </div>
              <button 
                onClick={handleRefresh}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-2"
                disabled={isRefreshing}
              >
                <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <RefreshCw className="animate-spin text-gray-400 mr-2" />
              <span>Loading transactions...</span>
            </div>
          ) : transactions && transactions.length > 0 ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      STATUS
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      TYPE
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CREATED
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PROCESSED
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr 
                      key={transaction.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedTransaction(transaction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(transaction.status === 'processing' ? 'received' : transaction.status)}
                          <span className={`ml-2 ${getStatusClass(transaction.status === 'processing' ? 'received' : transaction.status)}`}>
                            {transaction.status === 'processing' ? 'received' : transaction.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transaction.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm:ss')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transaction.processed_at 
                          ? format(new Date(transaction.processed_at), 'MMM dd, yyyy HH:mm:ss')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* Only show retry button for failed transactions - received/processing are handled by backend */}
                        {transaction.status === 'failed' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              processTransaction.mutate(transaction.id);
                            }}
                            className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded flex items-center gap-1"
                            disabled={isProcessing(transaction.id)}
                          >
                            {isProcessing(transaction.id) ? (
                              <>
                                <RefreshCw size={14} className="animate-spin" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} />
                                Retry
                              </>
                            )}
                          </button>
                        )}
                        {(transaction.status === 'received' || transaction.status === 'processing') && (
                          <span className="text-xs text-gray-500">
                            Auto-processing...
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <p className="text-gray-500">No transactions found</p>
            </div>
          )}
        </div>

        <div className="md:w-1/3">
          <Card>
            <div className="p-4">
              <h2 className="text-lg font-medium mb-4">Transaction Details</h2>
              {selectedTransaction ? (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Status</h3>
                      <div className="mt-1 flex items-center">
                        {getStatusIcon(selectedTransaction.status === 'processing' ? 'received' : selectedTransaction.status)}
                        <span className={`ml-2 ${getStatusClass(selectedTransaction.status === 'processing' ? 'received' : selectedTransaction.status)}`}>
                          {selectedTransaction.status === 'processing' ? 'received' : selectedTransaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500">Type</h3>
                    <p className="mt-1">{selectedTransaction.type}</p>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                    <p className="mt-1">{format(new Date(selectedTransaction.created_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                  </div>

                  {selectedTransaction.processed_at && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-500">Processed At</h3>
                      <p className="mt-1">{format(new Date(selectedTransaction.processed_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                    </div>
                  )}

                  {selectedTransaction.error && (
                    <div className="mb-4">
                      {selectedTransaction.error.toLowerCase().includes('successfully') ? (
                        <>
                          <h3 className="text-sm font-medium text-gray-500">Result</h3>
                          <p className="mt-1 text-green-600">{selectedTransaction.error}</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-sm font-medium text-gray-500">Error</h3>
                          <p className="mt-1 text-red-500">{selectedTransaction.error}</p>
                        </>
                      )}
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500">Payload</h3>
                    <pre className="mt-1 bg-gray-50 p-2 rounded text-xs overflow-auto max-h-96">
                      {JSON.stringify(selectedTransaction.payload, null, 2)}
                    </pre>
                  </div>

                  {selectedTransaction.status === 'failed' && (
                    <button 
                      onClick={() => processTransaction.mutate(selectedTransaction.id)}
                      className="w-full mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center justify-center gap-2"
                      disabled={isProcessing(selectedTransaction.id)}
                    >
                      {isProcessing(selectedTransaction.id) ? (
                        <>
                          <RefreshCw className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <RefreshCw />
                          Retry Processing
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-center">Select a transaction to view details</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}